import {useEffect, useRef, useState} from "react"
import {CopyPlus, Pencil, Plus, Printer, Save, Trash2, TriangleAlert,} from "lucide-react"
import type {
  OtkPrintProfile,
  PrintProfileBlankVersion,
  PrintProfileDocumentType,
  PrintProfileFieldLayout,
  PrintProfileFieldOffsets,
} from "shared"

import {DocumentPreviewCanvas} from "@/components/DocumentPreviewCanvas"
import {defaultCalibrationFields} from "@/components/documentPreviewFields"
import {Button} from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,} from "@/components/ui/dialog"
import {Input} from "@/components/ui/input"
import {Tooltip, TooltipContent, TooltipTrigger,} from "@/components/ui/tooltip"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {deletePrintProfile, loadPrintProfiles, savePrintProfile,} from "@/lib/api/printProfiles"

type ProfileMode = "view" | "edit"

type PrintProfileSummary = Pick<
  OtkPrintProfile,
  "id" | "name" | "documentType" | "blankVersion"
>

function newDraftProfileId() {
  return `print-profile-${Date.now()}`
}

/**
 * print-profiles.md — `/print-profiles`.
 *
 * §API/Дані п.7 (P2C): this page's calibration data (`fieldLayouts`/
 * `fieldOffsets`/`fieldScale`/`blankVersion`) is a standalone settings
 * screen, not wired into the real sign/print flow yet — see
 * `OPEN-QUESTIONS.md` п.116 and `DocumentPreviewDialog`
 * (details.md/document-editor.md), which prints via a plain
 * `window.print()` with no knowledge of print profiles.
 *
 * §D2 gap: the spec documents an automatic exit-guard on browser
 * navigation via React Router's `useBlocker`, which requires a data
 * router (`createBrowserRouter`/`RouterProvider`). This app mounts routes
 * with a plain `<BrowserRouter>` (see `App.tsx`) — `useBlocker` throws
 * under that setup, so it is intentionally **not** wired here. Only the
 * manual "Вийти" button path (which does not need `useBlocker`) is
 * implemented; navigating away via the sidebar/breadcrumbs with unsaved
 * changes will not prompt. Flagged to the user rather than silently
 * built around.
 *
 * Правило умовного відображення §"Dropdown «Профіль друку»" (2026-09-04):
 * раніше тут була окрема EmptyState-панель замість полотна, коли
 * `profiles.length === 0` — розбіжність із explicit-формулюванням спеки
 * ("EmptyState на цій сторінці не існує"). Виправлено: canvas завжди
 * рендериться з fallback-полями (`defaultCalibrationFields`), а dropdown
 * лише `disabled`.
 */
export function PrintProfiles() {
  const [profiles, setProfiles] = useState<PrintProfileSummary[]>([])
  const [selectedId, setSelectedId] = useState("")
  const [mode, setMode] = useState<ProfileMode>("view")
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [draftProfileName, setDraftProfileName] = useState("")
  const [draftBlankVersion, setDraftBlankVersion] =
    useState<PrintProfileBlankVersion>(2)
  const [fieldLayouts, setFieldLayouts] = useState<
    Record<string, PrintProfileFieldLayout[]>
  >({})
  const [fieldOffsets, setFieldOffsets] = useState<
    Record<string, PrintProfileFieldOffsets>
  >({})
  const [fieldScales, setFieldScales] = useState<Record<string, number>>({})
  const [profileRevision, setProfileRevision] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false)
  const [isSaveAsNewOpen, setIsSaveAsNewOpen] = useState(false)
  const [newProfileName, setNewProfileName] = useState("")
  const [actionError, setActionError] = useState<string | null>(null)
  const [editSnapshot, setEditSnapshot] = useState<{
    blankVersion: PrintProfileBlankVersion
    fieldLayouts: PrintProfileFieldLayout[]
    fieldOffsets: PrintProfileFieldOffsets
    fieldScale: number
    profileId: string
    profileName: string
  } | null>(null)
  const profileNameInputRef = useRef<HTMLInputElement>(null)
  const newProfileNameInputRef = useRef<HTMLInputElement>(null)

  const startTestPrint = () => {
    window.print()
  }

  const selectedProfile = profiles.find((profile) => profile.id === selectedId)
  const selectedFields = fieldLayouts[selectedId] ?? defaultCalibrationFields
  const selectedOffsets = fieldOffsets[selectedId] ?? { x: 0, y: 0 }
  const selectedFieldScale = fieldScales[selectedId] ?? 1
  const isPdfProfile = selectedProfile?.documentType === "ELECTRONIC_DOCUMENT"
  const canvasBlankVersion =
    mode === "edit"
      ? draftBlankVersion
      : (selectedProfile?.blankVersion ?? 2)
  const hasUnsavedChanges =
    mode === "edit" &&
    (isCreatingNew ||
      Boolean(
        editSnapshot &&
          editSnapshot.profileId === selectedId &&
          (draftProfileName !== editSnapshot.profileName ||
            draftBlankVersion !== editSnapshot.blankVersion ||
            selectedFieldScale !== editSnapshot.fieldScale ||
            selectedOffsets.x !== editSnapshot.fieldOffsets.x ||
            selectedOffsets.y !== editSnapshot.fieldOffsets.y ||
            JSON.stringify(selectedFields) !==
              JSON.stringify(editSnapshot.fieldLayouts))
      ))

  useEffect(() => {
    let isCancelled = false

    loadPrintProfiles()
      .then((storedProfiles) => {
        if (isCancelled) return

        setProfiles(
          storedProfiles.map((profile) => ({
            id: profile.id,
            name: profile.name,
            documentType: profile.documentType,
            blankVersion: profile.blankVersion,
          }))
        )
        setFieldLayouts(
          Object.fromEntries(
            storedProfiles.map((profile) => [
              profile.id,
              profile.fieldLayouts,
            ])
          )
        )
        setFieldOffsets(
          Object.fromEntries(
            storedProfiles.map((profile) => [
              profile.id,
              profile.fieldOffsets,
            ])
          )
        )
        setFieldScales(
          Object.fromEntries(
            storedProfiles.map((profile) => [profile.id, profile.fieldScale])
          )
        )
        setSelectedId(storedProfiles[0]?.id ?? "")
        setProfileRevision((revision) => revision + 1)
      })
      .catch((error) => {
        console.error("Не вдалося завантажити профілі друку", error)
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [])

  useEffect(() => {
    if (mode !== "edit") return
    const focusTimer = window.setTimeout(() => {
      profileNameInputRef.current?.focus()
      profileNameInputRef.current?.select()
    }, 0)
    return () => window.clearTimeout(focusTimer)
  }, [mode, selectedId])

  useEffect(() => {
    if (!isSaveAsNewOpen) return
    const focusTimer = window.setTimeout(() => {
      newProfileNameInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(focusTimer)
  }, [isSaveAsNewOpen])

  const updateSavedProfile = (savedProfile: OtkPrintProfile) => {
    setProfiles((current) => {
      const summary: PrintProfileSummary = {
        id: savedProfile.id,
        name: savedProfile.name,
        documentType: savedProfile.documentType,
        blankVersion: savedProfile.blankVersion,
      }
      return current.some((item) => item.id === summary.id)
        ? current.map((item) => (item.id === summary.id ? summary : item))
        : [...current, summary]
    })
    setFieldLayouts((current) => ({
      ...current,
      [savedProfile.id]: savedProfile.fieldLayouts,
    }))
    setFieldOffsets((current) => ({
      ...current,
      [savedProfile.id]: savedProfile.fieldOffsets,
    }))
    setFieldScales((current) => ({
      ...current,
      [savedProfile.id]: savedProfile.fieldScale,
    }))
  }

  const removeLocalProfile = (profileId: string) => {
    setProfiles((current) =>
      current.filter((profile) => profile.id !== profileId)
    )
    setFieldLayouts((current) => {
      const next = { ...current }
      delete next[profileId]
      return next
    })
    setFieldOffsets((current) => {
      const next = { ...current }
      delete next[profileId]
      return next
    })
    setFieldScales((current) => {
      const next = { ...current }
      delete next[profileId]
      return next
    })
  }

  const enterEditMode = () => {
    if (!selectedProfile) return
    setEditSnapshot({
      blankVersion: selectedProfile.blankVersion,
      fieldLayouts: selectedFields.map((field) => ({ ...field })),
      fieldOffsets: { ...selectedOffsets },
      fieldScale: selectedFieldScale,
      profileId: selectedProfile.id,
      profileName: selectedProfile.name,
    })
    setDraftProfileName(selectedProfile.name)
    setDraftBlankVersion(selectedProfile.blankVersion)
    setIsCreatingNew(false)
    setActionError(null)
    setMode("edit")
  }

  const createNewProfile = () => {
    const id = newDraftProfileId()
    setProfiles((current) => [
      ...current,
      {
        id,
        name: "",
        documentType: "PROTOCOL",
        blankVersion: 2,
      },
    ])
    setFieldLayouts((current) => ({
      ...current,
      [id]: defaultCalibrationFields.map((field) => ({ ...field })),
    }))
    setFieldOffsets((current) => ({
      ...current,
      [id]: { x: 0, y: 0 },
    }))
    setFieldScales((current) => ({
      ...current,
      [id]: 1,
    }))
    setSelectedId(id)
    setDraftProfileName("")
    setDraftBlankVersion(2)
    setIsCreatingNew(true)
    setEditSnapshot(null)
    setActionError(null)
    setMode("edit")
  }

  const exitEditMode = () => {
    if (isCreatingNew) {
      const fallbackProfile = profiles.find((profile) => profile.id !== selectedId)
      removeLocalProfile(selectedId)
      setSelectedId(fallbackProfile?.id ?? "")
    } else if (editSnapshot?.profileId === selectedId) {
      const snapshot = editSnapshot
      setDraftProfileName(snapshot.profileName)
      setDraftBlankVersion(snapshot.blankVersion)
      setFieldLayouts((current) => ({
        ...current,
        [selectedId]: snapshot.fieldLayouts.map((field) => ({ ...field })),
      }))
      setFieldOffsets((current) => ({
        ...current,
        [selectedId]: { ...snapshot.fieldOffsets },
      }))
      setFieldScales((current) => ({
        ...current,
        [selectedId]: snapshot.fieldScale,
      }))
    }

    setEditSnapshot(null)
    setIsExitConfirmOpen(false)
    setIsCreatingNew(false)
    setIsSaveAsNewOpen(false)
    setMode("view")
  }

  const requestExitEditMode = () => {
    if (hasUnsavedChanges) {
      setIsExitConfirmOpen(true)
      return
    }

    exitEditMode()
  }

  const selectProfile = (id: string) => {
    setSelectedId(id)
    setIsCreatingNew(false)
    setActionError(null)
    setMode("view")
  }

  const saveProfile = async () => {
    const name = draftProfileName.trim()
    if (!name || !selectedId) return

    const documentType: PrintProfileDocumentType =
      selectedProfile?.documentType ?? "PROTOCOL"
    const payload = {
      blankVersion: draftBlankVersion,
      documentType,
      fieldLayouts: selectedFields,
      fieldOffsets: selectedOffsets,
      fieldScale: selectedFieldScale,
      name,
    }
    console.log("Збереження профілю друку", payload)
    console.log("Збереження профілю друку", JSON.stringify(payload, null, 2))

    setIsSaving(true)
    setActionError(null)
    try {
      const draftProfileId = isCreatingNew ? selectedId : undefined
      const savedProfile = await savePrintProfile(
        payload,
        isCreatingNew ? undefined : selectedId
      )
      updateSavedProfile(savedProfile)
      if (draftProfileId) removeLocalProfile(draftProfileId)
      setSelectedId(savedProfile.id)
      setDraftProfileName(savedProfile.name)
      setDraftBlankVersion(savedProfile.blankVersion)
      setEditSnapshot({
        blankVersion: savedProfile.blankVersion,
        fieldLayouts: savedProfile.fieldLayouts.map((field) => ({ ...field })),
        fieldOffsets: { ...savedProfile.fieldOffsets },
        fieldScale: savedProfile.fieldScale,
        profileId: savedProfile.id,
        profileName: savedProfile.name,
      })
      setIsCreatingNew(false)
    } catch (error) {
      console.error("Не вдалося зберегти профіль друку", error)
      setActionError("Не вдалося зберегти профіль. Спробуйте ще раз.")
    } finally {
      setIsSaving(false)
    }
  }

  const saveAsNewProfile = async () => {
    const name = newProfileName.trim()
    if (!name) return

    const sourceProfileId = selectedId
    const wasCreatingNew = isCreatingNew
    const documentType: PrintProfileDocumentType =
      selectedProfile?.documentType ?? "PROTOCOL"
    const payload = {
      blankVersion: draftBlankVersion,
      documentType,
      fieldLayouts: selectedFields,
      fieldOffsets: selectedOffsets,
      fieldScale: selectedFieldScale,
      name,
    }
    console.log("Збереження профілю друку як нового", payload)

    setIsSaving(true)
    setActionError(null)
    try {
      const savedProfile = await savePrintProfile(payload)
      console.log("Новий профіль друку збережено", savedProfile)
      updateSavedProfile(savedProfile)
      if (wasCreatingNew && sourceProfileId !== savedProfile.id) {
        removeLocalProfile(sourceProfileId)
      }
      setSelectedId(savedProfile.id)
      setDraftProfileName(savedProfile.name)
      setNewProfileName("")
      setIsSaveAsNewOpen(false)
      setIsCreatingNew(false)
      setMode("view")
    } catch (error) {
      console.error("Не вдалося зберегти новий профіль друку", error)
      setActionError("Не вдалося зберегти профіль. Спробуйте ще раз.")
    } finally {
      setIsSaving(false)
    }
  }

  const removeCurrentProfile = async () => {
    if (!selectedProfile) return

    if (isCreatingNew) {
      const remaining = profiles.filter((profile) => profile.id !== selectedId)
      removeLocalProfile(selectedId)
      setSelectedId(remaining[0]?.id ?? "")
      setIsCreatingNew(false)
      setMode("view")
      return
    }

    setIsDeleting(true)
    setActionError(null)
    try {
      await deletePrintProfile(selectedId)
      const remaining = profiles.filter((profile) => profile.id !== selectedId)
      removeLocalProfile(selectedId)
      setSelectedId(remaining[0]?.id ?? "")
      setMode("view")
    } catch (error) {
      console.error("Не вдалося видалити профіль друку", error)
      setActionError("Не вдалося видалити профіль. Спробуйте ще раз.")
    } finally {
      setIsDeleting(false)
    }
  }

  const hasProfiles = profiles.length > 0

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 flex-wrap items-center gap-3 print:hidden">
        <h1 className="text-2xl font-semibold">
          Профілі друку та калібрування
        </h1>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {mode === "view" ? (
            hasProfiles && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="Видалити профіль"
                      className="rounded-full"
                      disabled={!selectedProfile || isDeleting || isSaving}
                      onClick={() => setIsDeleteConfirmOpen(true)}
                      size="icon-lg"
                      variant="destructive"
                    >
                      <Trash2 />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Видалити Профіль</TooltipContent>
                </Tooltip>
                <Button onClick={enterEditMode} size="lg" variant="outline">
                  <Pencil data-icon="inline-start" />
                  Редагувати
                </Button>
              </>
            )
          ) : (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Видалити профіль"
                    className="rounded-full"
                    disabled={isDeleting || isSaving}
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    size="icon-lg"
                    variant="destructive"
                  >
                    <Trash2 />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Видалити Профіль</TooltipContent>
              </Tooltip>
              <Button
                disabled={isSaving || isDeleting}
                onClick={() => setIsSaveAsNewOpen(true)}
                size="lg"
                variant="secondary"
              >
                <CopyPlus data-icon="inline-start" />
                Зберегти як новий…
              </Button>
              <Button
                disabled={
                  isSaving ||
                  isDeleting ||
                  !draftProfileName.trim() ||
                  !hasUnsavedChanges
                }
                onClick={() => void saveProfile()}
                size="lg"
              >
                <Save data-icon="inline-start" />
                {isSaving ? "Збереження…" : "Зберегти"}
              </Button>
              <Button
                disabled={isSaving || isDeleting}
                onClick={requestExitEditMode}
                size="lg"
                variant="secondary"
              >
                Вийти
              </Button>
            </>
          )}
        </div>
      </div>

      {actionError && (
        <p className="mb-3 shrink-0 text-sm text-destructive print:hidden">{actionError}</p>
      )}

      {isLoading ? null : (
        <div className="flex min-h-0 flex-1 flex-col gap-4 print:block print:h-auto">
          <div className="flex shrink-0 flex-wrap items-end gap-3 print:hidden">
            <div className="w-[300px] space-y-2">
              <label
                className="text-sm font-medium"
                htmlFor={mode === "edit" ? "print-profile-name" : "print-profile-select"}
              >
                Профіль друку:
              </label>
              {mode === "edit" ? (
                <Input
                  aria-label="Назва профілю друку"
                  id="print-profile-name"
                  onChange={(event) => setDraftProfileName(event.target.value)}
                  ref={profileNameInputRef}
                  value={draftProfileName}
                />
              ) : (
                <Select
                  disabled={profiles.length === 0}
                  onValueChange={selectProfile}
                  value={selectedId}
                >
                  <SelectTrigger
                    aria-label="Профіль друку"
                    className="w-full"
                    id="print-profile-select"
                  >
                    <SelectValue placeholder="Оберіть профіль" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name || "Новий профіль"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="w-[220px] space-y-2">
              <label
                className="text-sm font-medium"
                htmlFor="print-blank-version"
              >
                Бланк:
              </label>
              <Select
                disabled={mode === "view"}
                onValueChange={(value) =>
                  setDraftBlankVersion(Number(value) as PrintProfileBlankVersion)
                }
                value={String(canvasBlankVersion)}
              >
                <SelectTrigger
                  aria-label="Бланк"
                  className="w-full"
                  id="print-blank-version"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem disabled value="1">
                    Бланк версія №1
                  </SelectItem>
                  <SelectItem value="2">Бланк версія №2</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {mode === "view" && (
                <Button onClick={createNewProfile} variant="outline">
                  <Plus data-icon="inline-start" />
                  Створити Новий Профіль
                </Button>
              )}
              <Button onClick={startTestPrint} variant="outline">
                <Printer data-icon="inline-start" />
                Пробний друк
              </Button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 justify-center overflow-hidden rounded-lg border border-neutral-600 bg-neutral-700 print:block print:h-auto print:min-h-0 print:overflow-visible print:rounded-none print:border-0 print:bg-white">
            <DocumentPreviewCanvas
              calibration={mode === "edit"}
              fieldAutoSizeSource="examples"
              fields={selectedFields}
              key={`${selectedId}-${profileRevision}`}
              onFieldsChange={(fields) =>
                setFieldLayouts((current) => ({
                  ...current,
                  [selectedId]: fields,
                }))
              }
              offsets={selectedOffsets}
              onOffsetsChange={(offsets) =>
                setFieldOffsets((current) => ({
                  ...current,
                  [selectedId]: offsets,
                }))
              }
              elementScale={selectedFieldScale}
              onElementScaleChange={(scale) =>
                setFieldScales((current) => ({
                  ...current,
                  [selectedId]: scale,
                }))
              }
              scanSrc={
                !isPdfProfile && canvasBlankVersion === 2
                  ? "/assets/blank-protocol-v2-front.jpg"
                  : null
              }
              showBlankControl={!isPdfProfile}
              showFieldBlocks
              showFieldContentControls
              showFieldFontSizeControl={mode === "edit"}
              showFieldList={mode === "edit"}
              showMockContent={false}
            />
          </div>
        </div>
      )}

      <Dialog
        onOpenChange={(open) => {
          setIsSaveAsNewOpen(open)
          if (!open) setNewProfileName("")
        }}
        open={isSaveAsNewOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Зберегти як Новий Профіль Друку</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="new-profile-name">
              Назва Профілю:
            </label>
            <Input
              id="new-profile-name"
              onChange={(event) => setNewProfileName(event.target.value)}
              ref={newProfileNameInputRef}
              value={newProfileName}
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsSaveAsNewOpen(false)
                setNewProfileName("")
              }}
              variant="outline"
            >
              Назад
            </Button>
            <Button
              disabled={isSaving || !newProfileName.trim()}
              onClick={() => void saveAsNewProfile()}
            >
              <Save data-icon="inline-start" />
              {isSaving ? "Збереження…" : "Зберегти"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        onOpenChange={setIsExitConfirmOpen}
        open={isExitConfirmOpen}
      >
        <AlertDialogContent className="data-[size=default]:sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-warning/10 text-warning">
              <TriangleAlert />
            </AlertDialogMedia>
            <AlertDialogTitle>Вийти без збереження?</AlertDialogTitle>
            <AlertDialogDescription>
              У профілі є незбережені зміни. Якщо вийти зараз, усі зміни буде втрачено.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:grid sm:grid-cols-2">
            <AlertDialogCancel className="w-full">
              Продовжити редагування
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full"
              onClick={exitEditMode}
              variant="destructive"
            >
              Вийти без збереження
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        onOpenChange={setIsDeleteConfirmOpen}
        open={isDeleteConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-destructive/10 text-destructive">
              <TriangleAlert />
            </AlertDialogMedia>
            <AlertDialogTitle>Видалити Профіль</AlertDialogTitle>
            <AlertDialogDescription>
              Профіль «{selectedProfile?.name ?? "поточний профіль"}» буде видалено. Цю дію неможливо скасувати.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Назад</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting || isSaving}
              onClick={() => void removeCurrentProfile()}
              variant="destructive"
            >
              <Trash2 data-icon="inline-start" />
              Видалити Профіль
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
